import { useState } from "preact/hooks";

import type { Profile } from "../lib/types";

interface Props {
  profiles: Profile[];
  onChange: (profiles: Profile[]) => void;
}

/** Structured Profiles editor — replaces the raw JSON textarea.
 * Each profile is a card with a name input and a chip-style keyword list. */
export function ProfilesEditor({ profiles, onChange }: Props) {
  function update(idx: number, patch: Partial<Profile>) {
    onChange(profiles.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }
  function remove(idx: number) {
    onChange(profiles.filter((_, i) => i !== idx));
  }
  function add() {
    onChange([...profiles, { name: "New Profile", keywords: [] }]);
  }

  return (
    <div class="profiles-editor">
      {profiles.map((p, idx) => (
        <ProfileCard
          key={idx}
          profile={p}
          onUpdate={(patch) => update(idx, patch)}
          onRemove={() => remove(idx)}
        />
      ))}
      <button class="btn-primary add-profile-btn" onClick={add}>+ Add Profile</button>
    </div>
  );
}

function ProfileCard({
  profile,
  onUpdate,
  onRemove,
}: {
  profile: Profile;
  onUpdate: (patch: Partial<Profile>) => void;
  onRemove: () => void;
}) {
  const [newKw, setNewKw] = useState("");

  function addKeyword() {
    const v = newKw.trim();
    if (!v) return;
    if (profile.keywords.includes(v)) {
      setNewKw("");
      return;
    }
    onUpdate({ keywords: [...profile.keywords, v] });
    setNewKw("");
  }
  function removeKeyword(kw: string) {
    onUpdate({ keywords: profile.keywords.filter((k) => k !== kw) });
  }

  return (
    <div class="profile-card">
      <div class="profile-card-header">
        <input
          class="form-control profile-name-input"
          value={profile.name}
          onInput={(e) => onUpdate({ name: e.currentTarget.value })}
          aria-label="Profile name"
        />
        <button class="row-delete" onClick={onRemove} aria-label={`Delete ${profile.name}`} title="Delete">×</button>
      </div>
      <div class="profile-keywords">
        {profile.keywords.map((kw) => (
          <span class="keyword-chip" key={kw}>
            {kw}
            <button class="keyword-remove" onClick={() => removeKeyword(kw)} aria-label={`Remove ${kw}`}>×</button>
          </span>
        ))}
        <div class="keyword-input-row">
          <input
            class="form-control"
            placeholder="Add keyword…"
            value={newKw}
            onInput={(e) => setNewKw(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addKeyword();
              }
            }}
            aria-label="Add keyword"
          />
          <button class="platform-btn" onClick={addKeyword} disabled={!newKw.trim()}>Add</button>
        </div>
      </div>
    </div>
  );
}
